/**
 * Toast Notification Utility
 * Provides user feedback for actions throughout the application
 *
 * @module utils/toast
 */

import toast from 'react-hot-toast';

/**
 * Toast notification configuration
 */
const DEFAULT_OPTIONS = {
  // Position on screen
  position: 'top-center',

  // Duration in milliseconds
  duration: 4000,

  // Styling
  style: {
    background: '#333',
    color: '#fff',
    fontSize: '14px',
    borderRadius: '8px',
    padding: '12px 20px',
    maxWidth: '500px',
  },

  // Accessibility
  ariaProps: {
    role: 'status',
    'aria-live': 'polite',
  },
};

/**
 * Show success toast notification
 * @param {string} message - Success message to display
 * @param {Object} options - Optional toast configuration
 * @returns {string} Toast ID for dismissal
 */
export const showSuccess = (message, options = {}) => {
  return toast.success(message, {
    ...DEFAULT_OPTIONS,
    duration: 3000,
    icon: '✅',
    style: {
      ...DEFAULT_OPTIONS.style,
      background: '#10b981',
      color: '#fff',
    },
    ...options,
  });
};

/**
 * Show error toast notification
 * @param {string} message - Error message to display
 * @param {Object} options - Optional toast configuration
 * @returns {string} Toast ID for dismissal
 */
export const showError = (message, options = {}) => {
  return toast.error(message, {
    ...DEFAULT_OPTIONS,
    duration: 5000,
    icon: '❌',
    style: {
      ...DEFAULT_OPTIONS.style,
      background: '#ef4444',
      color: '#fff',
    },
    ariaProps: {
      role: 'alert',
      'aria-live': 'assertive',
    },
    ...options,
  });
};

/**
 * Show info toast notification
 * @param {string} message - Info message to display
 * @param {Object} options - Optional toast configuration
 * @returns {string} Toast ID for dismissal
 */
export const showInfo = (message, options = {}) => {
  return toast(message, {
    ...DEFAULT_OPTIONS,
    duration: 4000,
    icon: 'ℹ️',
    style: {
      ...DEFAULT_OPTIONS.style,
      background: '#3b82f6',
      color: '#fff',
    },
    ...options,
  });
};

/**
 * Show warning toast notification
 * @param {string} message - Warning message to display
 * @param {Object} options - Optional toast configuration
 * @returns {string} Toast ID for dismissal
 */
export const showWarning = (message, options = {}) => {
  return toast(message, {
    ...DEFAULT_OPTIONS,
    duration: 4500,
    icon: '⚠️',
    style: {
      ...DEFAULT_OPTIONS.style,
      background: '#f59e0b',
      color: '#fff',
    },
    ...options,
  });
};

/**
 * Show loading toast notification
 * Use this for actions that take time (API calls, etc.)
 * Remember to dismiss it when done: toast.dismiss(loadingId)
 *
 * @param {string} message - Loading message to display
 * @param {Object} options - Optional toast configuration
 * @returns {string} Toast ID for dismissal
 */
export const showLoading = (message, options = {}) => {
  return toast.loading(message, {
    ...DEFAULT_OPTIONS,
    duration: Infinity, // Don't auto-dismiss loading toasts
    style: {
      ...DEFAULT_OPTIONS.style,
      background: '#6366f1',
      color: '#fff',
    },
    ...options,
  });
};

/**
 * Show promise-based toast
 * Automatically shows loading, then success/error based on promise result
 *
 * @param {Promise} promise - Promise to track
 * @param {Object} messages - Messages for loading, success, error states
 * @param {Object} options - Optional toast configuration
 * @returns {Promise} Original promise
 *
 * @example
 * showPromise(
 *   fetchData(),
 *   {
 *     loading: 'Loading data...',
 *     success: 'Data loaded successfully!',
 *     error: 'Failed to load data'
 *   }
 * );
 */
export const showPromise = (promise, messages, options = {}) => {
  return toast.promise(
    promise,
    {
      loading: messages.loading || 'Loading...',
      success: messages.success || 'Success!',
      error: (err) => messages.error || err?.message || 'Something went wrong',
    },
    {
      ...DEFAULT_OPTIONS,
      ...options,
    }
  );
};

/**
 * Dismiss a specific toast by ID
 * @param {string} toastId - ID returned from show* functions
 */
export const dismissToast = (toastId) => {
  toast.dismiss(toastId);
};

/**
 * Dismiss all active toasts
 */
export const dismissAllToasts = () => {
  toast.dismiss();
};

/**
 * Custom toast with full control
 * @param {string} message - Message to display
 * @param {Object} options - Toast configuration
 * @returns {string} Toast ID for dismissal
 */
export const showCustom = (message, options = {}) => {
  return toast(message, {
    ...DEFAULT_OPTIONS,
    ...options,
  });
};

/**
 * Predefined toasts for common scenarios
 */
export const ToastMessages = {
  // Video ad related
  VIDEO_LOADING: 'Loading advertisement...',
  VIDEO_READY: 'Video ready! Watch to connect to WiFi.',
  VIDEO_FAILED: 'Failed to load video. Please try again.',
  VIDEO_UNAVAILABLE: 'Video ad unavailable. You can continue to connect.',
  VIDEO_INCOMPLETE: 'Please watch the complete video to continue.',
  VIDEO_COMPLETED: 'Video watched! Connecting to WiFi...',

  // Connection related
  CONNECTING: 'Connecting to WiFi...',
  CONNECTED: 'Successfully connected to WiFi!',
  CONNECTION_FAILED: 'Failed to connect. Please try again.',
  ALREADY_ONLINE: 'You are already connected to WiFi.',

  // Authentication related
  AUTH_IN_PROGRESS: 'Authenticating...',
  AUTH_SUCCESS: 'Authentication successful!',
  AUTH_FAILED: 'Authentication failed. Please try again.',

  // Session related
  SESSION_EXPIRED: 'Your session has expired. Please refresh.',
  SESSION_INVALID: 'Invalid session. Please try again.',

  // Network related
  NETWORK_ERROR: 'Network error. Please check your connection.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
  SERVER_ERROR: 'Server error. Please try again later.',

  // General
  LOADING: 'Loading...',
  PLEASE_WAIT: 'Please wait...',
  TRY_AGAIN: 'Something went wrong. Please try again.',
};

// Export default toast instance for advanced usage
export { toast };

export default {
  success: showSuccess,
  error: showError,
  info: showInfo,
  warning: showWarning,
  loading: showLoading,
  promise: showPromise,
  dismiss: dismissToast,
  dismissAll: dismissAllToasts,
  custom: showCustom,
  messages: ToastMessages,
};
